/**
 * The UI skin, injected as one stylesheet at boot.
 *
 * Diegetic and angular: parallelogram-cut bars, hammered-metal panel edges,
 * engraved serif small-caps headers, wax-seal buttons. No rounded-rect flat
 * cards anywhere. Everything sits on dark parchment or slate.
 */

import { toCss, C } from '../data/palette.ts';

export const UI_CSS = `
:root {
  --ash: ${toCss(C.ash)};
  --ash-dark: ${toCss(C.ashDark)};
  --ash-deep: ${toCss(C.ashDeep)};
  --bone: ${toCss(C.bone)};
  --bone-dim: ${toCss(C.boneDim)};
  --bone-deep: ${toCss(C.boneDeep)};
  --gold: ${toCss(C.gold)};
  --gold-pale: ${toCss(C.goldPale)};
  --gold-deep: ${toCss(C.goldDeep)};
  --oxblood: ${toCss(C.oxblood)};
  --oxblood-light: ${toCss(C.oxbloodLight)};
  --ember: ${toCss(C.ember)};
  --plague: ${toCss(C.plague)};
  --will: ${toCss(C.will)};
  --panel: rgba(18, 16, 14, 0.93);
  --panel-edge: rgba(200, 164, 76, 0.30);
}

#ui-root {
  font-family: 'Inter', system-ui, -apple-system, sans-serif;
  font-size: 13px;
  color: var(--bone);
  user-select: none;
  -webkit-user-select: none;
}
#ui-root .panel {
  position: absolute;
  background: var(--panel);
  border: 1px solid var(--panel-edge);
  box-shadow: inset 0 1px 0 rgba(217, 207, 188, 0.08), 0 8px 26px rgba(0, 0, 0, 0.6);
  pointer-events: auto;
}
/* Hammered-metal edge: a bright top lip and a dark underside. */
#ui-root .panel::before {
  content: '';
  position: absolute;
  inset: 0;
  pointer-events: none;
  border-top: 1px solid rgba(217, 207, 188, 0.10);
  border-bottom: 1px solid rgba(0, 0, 0, 0.5);
}
#ui-root h1, #ui-root h2, #ui-root h3, #ui-root .hdr {
  font-family: 'Cinzel', Georgia, serif;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.16em;
  color: var(--gold);
  margin: 0;
}
#ui-root .hdr {
  font-size: 11px;
  padding: 6px 10px 5px;
  border-bottom: 1px solid rgba(200, 164, 76, 0.18);
  background: linear-gradient(180deg, rgba(200,164,76,0.09), rgba(0,0,0,0));
}
#ui-root .dim { color: var(--bone-deep); }
#ui-root .num { font-variant-numeric: tabular-nums; }

/* ── Resource strip ──────────────────────────────────────────────────────── */
#res-bar {
  top: 0; left: 0; right: 0;
  height: 40px;
  /*
   * Above the world map.
   *
   * This strip is the one piece of furniture that is on screen in every mode
   * and never moves — that sentence has been in this file since the chrome bar
   * landed, and it was not true: the realm map was absolutely positioned over the
   * whole viewport, so the strip was on screen in every mode *except the one
   * the player spends half the game in*. The map starts below it now (see the
   * top offset on #worldmap in worldmap.ts) and this is what puts the strip in
   * front of the two overlays that are drawn edge to edge — the Ledger and the
   * hero sheet still sit above it, at 75 and 70, because those are screens
   * rather than furniture.
   */
  z-index: 55;
  display: flex;
  align-items: stretch;
  gap: 0;
  background: linear-gradient(180deg, rgba(14,13,11,0.97), rgba(14,13,11,0.82));
  border-bottom: 1px solid var(--panel-edge);
  padding-left: 6px;
}
.res {
  display: flex;
  align-items: center;
  gap: 6px;
  /* Every chip on this strip explains itself on hover, and says so. */
  cursor: help;
  padding: 0 13px 0 10px;
  /* Parallelogram cut — the shape rule for every bar in this game. */
  clip-path: polygon(8px 0, 100% 0, calc(100% - 8px) 100%, 0 100%);
  background: rgba(255,255,255,0.018);
}
/* The mark. Wide enough for a drawn icon, and still the old character box for
   the late-Age resources that have not been drawn yet — see ui/resicons.ts.
   No backticks in this file: it is one template literal, and a backtick in a
   comment ends the stylesheet. */
.res .glyph { color: var(--gold-deep); font-size: 14px; width: 15px; height: 15px;
  display: flex; align-items: center; justify-content: center; }
.ricon { display: block; }
/* An icon inside a run of text — a build cost, a muster price — sits on the
   baseline rather than on the line box, or the row grows by three pixels and
   the whole list breathes as costs come and go. */
.rglyph { font-size: 12px; color: var(--gold-deep); }
.bcost .ricon, .mc .ricon, .bi-row .ricon { display: inline-block; vertical-align: -2px; margin-right: 1px; }
.res .val { font-variant-numeric: tabular-nums; font-size: 14px; letter-spacing: 0.02em; }
.res .rate { font-size: 10px; color: var(--bone-deep); font-variant-numeric: tabular-nums; }
.res.low .val { color: var(--oxblood-light); }
.res.capped .val { color: var(--gold-pale); }

/* ── The alert cluster ───────────────────────────────────────────────────────
 * What is wrong, on the one bar that is on screen in every mode. Dire pulses
 * and warning does not: a thing that is always moving is a thing the eye stops
 * seeing, so the animation is spent on the handful of states that are actually
 * an emergency. See ui/alerts.ts for what earns a chip. */
#alerts { display: flex; align-items: center; gap: 3px; padding: 0 8px;
  border-left: 1px solid rgba(200,164,76,0.18); }
#alerts:empty { display: none; }
.alert {
  display: flex; align-items: center; justify-content: center;
  width: 27px; height: 27px; cursor: pointer;
  clip-path: polygon(5px 0, 100% 0, calc(100% - 5px) 100%, 0 100%);
  transition: background 110ms ease, filter 110ms ease;
}
.alert svg { display: block; }
.alert.warn { color: var(--gold); background: rgba(200,164,76,0.13); }
.alert.warn:hover { background: rgba(200,164,76,0.30); }
.alert.dire { color: #e8746b; background: rgba(110,31,34,0.42); animation: alert-beat 1.5s ease-in-out infinite; }
.alert.dire:hover { background: rgba(156,51,54,0.7); }
@keyframes alert-beat {
  0%, 100% { filter: brightness(0.82); }
  50% { filter: brightness(1.45); }
}

#phase-box {
  margin-left: auto;
  display: flex; align-items: center; gap: 14px;
  padding: 0 16px;
  border-left: 1px solid rgba(200,164,76,0.18);
}

#phase-box .clock { display: flex; align-items: center; gap: 14px; cursor: help; }
#phase-name { font-family: 'Cinzel', Georgia, serif; letter-spacing: 0.2em; text-transform: uppercase; font-size: 12px; color: var(--gold); }
#phase-timer { font-variant-numeric: tabular-nums; font-size: 20px; letter-spacing: 0.04em; }
#phase-timer.urgent { color: var(--oxblood-light); }
#wave-label { font-size: 11px; letter-spacing: 0.18em; text-transform: uppercase; color: var(--bone-deep); }

/* The tax dial. Five notches, because the band is the whole decision: every
   notch of revenue is bought with goodwill, and the player should be able to
   see how far along that trade they are standing without opening anything.
   Notches past Fair go oxblood — those are the ones the people pay for. */
.taxdial {
  display: flex; align-items: center; gap: 7px;
  padding: 0 13px 0 10px;
  cursor: help;
  clip-path: polygon(8px 0, 100% 0, calc(100% - 8px) 100%, 0 100%);
  background: rgba(255,255,255,0.018);
}
.taxdial .tk { font-size: 10px; letter-spacing: 0.14em; text-transform: uppercase; color: var(--bone-deep); }
.taxdial .tn { display: flex; gap: 2px; align-items: flex-end; }
.taxdial .tn i {
  display: block; width: 7px; height: 15px; background: rgba(217,207,188,0.12);
  clip-path: polygon(2px 0, 100% 0, calc(100% - 2px) 100%, 0 100%);
}
.taxdial .tn i.on { background: var(--gold); }
.taxdial .tn i.on.hard { background: var(--oxblood-light); }
.taxdial.live .tn i { cursor: pointer; }
.taxdial.live .tn i:hover { background: var(--gold-pale); }

/* ── Wax-seal buttons ────────────────────────────────────────────────────── */
.seal {
  position: relative;
  display: inline-flex; align-items: center; justify-content: center;
  padding: 7px 15px;
  font-family: 'Cinzel', Georgia, serif;
  font-size: 11px; letter-spacing: 0.15em; text-transform: uppercase;
  color: var(--bone);
  background: linear-gradient(180deg, rgba(110,31,34,0.95), rgba(70,18,20,0.95));
  border: 1px solid rgba(200,164,76,0.35);
  clip-path: polygon(7px 0, 100% 0, calc(100% - 7px) 100%, 0 100%);
  cursor: pointer;
  transition: filter 120ms ease, transform 90ms ease;
}
.seal:hover { filter: brightness(1.3); }
.seal:active { transform: translateY(1px); }
.seal.disabled { filter: grayscale(0.8) brightness(0.55); cursor: not-allowed; }
.seal.ghost {
  background: linear-gradient(180deg, rgba(40,37,33,0.9), rgba(22,20,18,0.9));
  border-color: rgba(200,164,76,0.22);
}

/* ── Build menu ──────────────────────────────────────────────────────────── */
#build-panel { left: 0; bottom: 0; width: 306px; top: 40px; display: flex; flex-direction: column; }
#build-cats { display: flex; flex-wrap: wrap; gap: 2px; padding: 6px; }
.cat {
  flex: 1 1 auto; text-align: center; padding: 5px 6px; font-size: 10px;
  letter-spacing: 0.12em; text-transform: uppercase; cursor: pointer;
  background: rgba(255,255,255,0.03); color: var(--bone-deep);
  clip-path: polygon(5px 0, 100% 0, calc(100% - 5px) 100%, 0 100%);
}
.cat.on { background: rgba(200,164,76,0.16); color: var(--gold-pale); }
/* Relatively positioned so a heading's offsetTop is measured against the box
 * that scrolls — that is what lets the list open on the current Age. */
#build-list { overflow-y: auto; flex: 1; padding: 0 6px 8px; position: relative; }
.bitem {
  display: flex; gap: 8px; align-items: center;
  padding: 5px 8px; margin-bottom: 2px; cursor: pointer;
  background: rgba(255,255,255,0.022);
  border-left: 2px solid transparent;
}
.bitem:hover { background: rgba(200,164,76,0.10); border-left-color: var(--gold-deep); }
.bitem.on { background: rgba(200,164,76,0.17); border-left-color: var(--gold); }
.bitem.cant { opacity: 0.42; }
/* The building's own art, in the settlement's own vernacular. flex:none, or a
   flex child holding an image gives its width up to the text beside it and
   collapses to a sliver — the same lesson as the roster's head crop.
   object-fit:contain because a granary is 3x2 and a tower is 1x1, and a
   catalogue where every picture is stretched to a square is a catalogue of
   smears. bottom, because a building is drawn with its roof in the padding
   above the footprint and centring it hangs every one of them in mid-air. */
.bitem .bthumb { flex: none; width: 46px; height: 38px; display: flex;
  align-items: flex-end; justify-content: center; }
/* Lifted, because the art is lit for a screen where it sits on ash and here it
   sits on a panel that is darker than the ground it was drawn against. */
.bitem .bthumb img { max-width: 100%; max-height: 100%; object-fit: contain;
  filter: brightness(1.22) drop-shadow(0 1px 2px rgba(0,0,0,0.75)); }
.bitem .bbody { flex: 1; min-width: 0; }
.bitem .bname { font-size: 12px; color: var(--bone); }
.bitem .bcost { font-size: 10.5px; color: var(--bone-deep); font-variant-numeric: tabular-nums; margin-top: 2px; }
.bitem .bcost .short { color: var(--oxblood-light); }
.bitem .bflav { font-size: 10.5px; color: var(--bone-deep); font-style: italic; margin-top: 3px; line-height: 1.35; }

/* The door to the Ledger, pinned under the build list. */
#ledger-btn { margin: 0 6px 8px; }

/* ── The chrome bar ──────────────────────────────────────────────────────────
 * Pause, speed, sound, the sheet, the Ledger, the realm and the key list. Every
 * one of them is a key that already worked; the button is so that a player who
 * never touches the keyboard can still reach all of it, and the little grey
 * letter on each is so that a player who wants the key can find out what it is
 * without leaving the game. */
#ctl { display: flex; align-items: stretch; padding: 0 8px;
  border-left: 1px solid rgba(200,164,76,0.18); }
/* Three groups: how fast the world runs, what it sounds like, and what opens.
   The gap between groups is four times the gap inside one, which is the whole
   of how a row of ten buttons becomes three things to choose between. */
.ctl-group { display: flex; align-items: stretch; gap: 2px; }
.ctl-group + .ctl-group { margin-left: 13px; padding-left: 13px;
  border-left: 1px solid rgba(200,164,76,0.12); }
.ctl {
  position: relative; display: flex; flex-direction: column; align-items: center;
  justify-content: center; min-width: 32px; padding: 0 7px; cursor: pointer;
  background: rgba(255,255,255,0.02);
  clip-path: polygon(5px 0, 100% 0, calc(100% - 5px) 100%, 0 100%);
  transition: background 110ms ease, color 110ms ease;
}
.ctl:hover { background: rgba(200,164,76,0.16); }
.ctl .cg { font-size: 13px; line-height: 1; color: var(--bone-dim);
  display: flex; align-items: center; height: 16px; }
.ctl .ck { font-size: 8.5px; letter-spacing: 0.06em; color: var(--bone-deep); margin-top: 3px; }
/* A named button says what it opens. The key hint moves up beside the mark,
   because two lines of text under a 16px icon does not fit in a 40px bar and
   the word is worth more than the letter. */
.ctl.named { min-width: 0; padding: 0 9px; }
.ctl.named .cn { font-family: 'Cinzel', Georgia, serif; font-size: 9px;
  letter-spacing: 0.13em; text-transform: uppercase; color: var(--bone-dim);
  margin-top: 3px; }
.ctl.named .ck { position: absolute; top: 3px; right: 3px; margin-top: 0;
  font-size: 8px; color: rgba(109,103,94,0.8); }
.ctl.named:hover .cn { color: var(--gold-pale); }
.ctl.on .cg { color: var(--gold-pale); }
.ctl.on { background: rgba(200,164,76,0.20); }
.ctl.on .cn { color: var(--gold-pale); }
.ctl.off .cg { color: var(--oxblood-light); }
.ctl.speed { cursor: help; min-width: 30px; }
.ctl.speed .cg { font-size: 12px; font-variant-numeric: tabular-nums; }
/* The horn wears its key like everything else on this strip. */
#phase-box .seal .kb { font-size: 8.5px; letter-spacing: 0.06em; color: rgba(217,207,188,0.55);
  border: 1px solid rgba(217,207,188,0.22); padding: 0 4px; margin-left: 8px; }

/* ── The realm screen keeps the furniture and loses the town ─────────────────
 *
 * One class, set by updateChrome when the world map is the screen being
 * looked at. Everything the strip carries that is true of the *realm* stays —
 * the resource chips (reading the crown's purse), the people chip (reading its
 * levies), the alert cluster, the clock and the whole chrome bar. Two things go.
 *
 * **The tax dial** goes, and it goes in updateTax rather than here — it is one
 * property with one authority, and this rule lost to the inline display that
 * the battle-mode switch writes on the first frame of every run.
 *
 * **The horn** goes too, on its own class and on both maps: there is nothing to
 * call in anywhere in a campaign — a capital's assaults arrive off the map
 * rather than off a clock, which is what updatePhase has always said in words —
 * and a disabled button that exists to say so is not worth the width. It comes
 * straight back for a battle, where it is what ends deployment.
 *
 * The build menu, the roster, the cards, the Powers and the chart are not
 * listed because they do not need to be: the map is drawn over them.
 */
#ui-root.no-horn #phase-box .seal { display: none; }

/* ── Narrow screens ──────────────────────────────────────────────────────────
 *
 * The desktop HUD is three fixed columns — a 306px build menu, a 268px roster
 * and a 268px chart — which is 842 pixels of furniture. On a 390-pixel phone
 * they do not merely crowd each other: they overlap, they cover the canvas
 * corner to corner, and there is no game visible behind them at all. A finger
 * put anywhere on the screen lands on a panel, which is why the touch layer
 * could not even be tested until this existed.
 *
 * So on a narrow screen the columns stop being columns. Every one of them
 * becomes a **sheet**: full width, half the height, along the bottom edge, and
 * *shut* unless its tab is chosen. The map is the screen; a panel is something
 * you pull up over it and put away again. That is the arrangement every
 * strategy game that has made this crossing arrives at, and it is arrived at
 * for this reason.
 *
 * The breakpoint is a width, not a device: a desktop window dragged narrow gets
 * the same treatment, which is the only way this stays honest — it can be
 * tested without a phone.
 */
#ui-root.narrow #build-panel,
#ui-root.narrow #binfo,
#ui-root.narrow #roster,
#ui-root.narrow #minimap {
  left: 0; right: 0; width: auto;
  top: auto; bottom: 52px; height: 50vh;
  display: none;
  border-bottom: none;
}
#ui-root.narrow.sheet-build #build-panel { display: flex; }
#ui-root.narrow.sheet-build #binfo { display: none; }
/* A building the player has tapped wins the sheet from the catalogue, the same
   way it wins the column on a desktop — they are the same question about the
   same corner, and only one of them is ever the one you want. */
#ui-root.narrow.sheet-build.binfo-open #build-panel { display: none; }
#ui-root.narrow.sheet-build.binfo-open #binfo { display: flex; }
#ui-root.narrow.sheet-army #roster { display: flex; }
#ui-root.narrow.sheet-map #minimap { display: block; }
#ui-root.narrow #minimap-cv { width: 100% !important; height: auto !important;
  aspect-ratio: 1; max-height: calc(50vh - 40px); margin: 6px auto; }

/* The tab bar. The only thing on this layout that is always up, because it is
   the way back to everything that is not. */
#tabbar { display: none; }
#ui-root.narrow #tabbar {
  position: absolute; left: 0; right: 0; bottom: 0; height: 52px; z-index: 20;
  display: flex; align-items: stretch; pointer-events: auto;
  background: linear-gradient(0deg, rgba(14,13,11,0.98), rgba(14,13,11,0.9));
  border-top: 1px solid var(--panel-edge);
}
#tabbar .tb {
  flex: 1 1 0; display: flex; flex-direction: column; align-items: center;
  justify-content: center; gap: 3px; cursor: pointer; color: var(--bone-deep);
  font-family: 'Cinzel', Georgia, serif; font-size: 9px; letter-spacing: 0.12em;
  text-transform: uppercase;
  border-right: 1px solid rgba(200,164,76,0.12);
}
#tabbar .tb:last-child { border-right: none; }
#tabbar .tb svg { display: block; }
/* Five tabs in 390 pixels is 78 each, and "Ledger" at this tracking is 44 of
   them — but a word that wraps would take the row to three lines and shove the
   marks off centre, so it is held on one and allowed to be clipped instead. */
#tabbar .tb span { white-space: nowrap; }
#tabbar .tb.on { color: var(--gold-pale); background: rgba(200,164,76,0.16); }

/* ── The bottom edge, as one ladder ──────────────────────────────────────────
 *
 * Six things want the bottom of a phone: the tab bar, the card bar, the Power
 * bar, the Will gauge, the hero's skill row and the order bar. The first cut
 * gave each its own hard-coded offset and a second copy of that offset for the
 * cards-on case, and it collided immediately — the order bar and the Will
 * gauge were both written at 134, so a seventy-nine-pixel panel was drawn over
 * a seven-pixel line and the one readout that says whether you can afford a
 * Power was underneath it.
 *
 * So the offsets are declared once, as a rung, and every tier is that rung plus
 * a constant. Two classes move the rung: 'cards-on' puts the card bar in the
 * first tier and pushes everything up by one, and 'hero-on' opens a tier for
 * the skill row that closes again when the run has no hero in it — otherwise
 * the order bar floats over an empty band on every run that never picked one.
 *
 * 'transform: none' is not decoration. '#powers', '#will-bar' and '#will-label'
 * are all centred on the desktop with 'left: 50%' and a translate, and a rule
 * that only overwrites 'left' leaves the translate in place: measured on a
 * 390px screen the Will bar was drawn at **x = −179**, nearly all of it off the
 * left edge. #powers had already been given the reset; the other two had not.
 */
#ui-root.narrow { --tier: 56px; --hero: 0px; }
#ui-root.narrow.cards-on { --tier: 160px; }
#ui-root.narrow.hero-on { --hero: 62px; }

/* A sheet owns the bottom half while it is up. See 'setSheet' in hud.ts. */
#ui-root.narrow.sheet-on #cards,
#ui-root.narrow.sheet-on #powers,
#ui-root.narrow.sheet-on #will-bar,
#ui-root.narrow.sheet-on #will-label,
#ui-root.narrow.sheet-on #hero-panel,
#ui-root.narrow.sheet-on #orders { display: none; }

#ui-root.narrow #cards { left: 4px; right: 4px; bottom: 56px; }
#ui-root.narrow #powers { left: 4px; right: 4px; transform: none; bottom: var(--tier);
  justify-content: safe center; overflow-x: auto; }
#ui-root.narrow #will-bar { left: 8px; right: 8px; width: auto; transform: none;
  bottom: calc(var(--tier) + 78px); }
#ui-root.narrow #will-label { left: 8px; right: auto; transform: none;
  bottom: calc(var(--tier) + 88px); }
/* Five orders, a name and a hint do not fit on one 390-pixel line, so the bar
   wraps — and the two things that are not buttons are given whole lines of
   their own, because a name sharing a row with the first two buttons and a hint
   sharing one with the last is a bar that looks broken rather than wrapped. */
#ui-root.narrow #orders { left: 4px; right: 4px;
  bottom: calc(var(--tier) + 104px + var(--hero)); flex-wrap: wrap; }
#ui-root.narrow #orders .ow,
#ui-root.narrow #orders .ohint { flex: 1 0 100%; margin: 0; }
/* ':not(.sheet-on)' rather than a heavier selector, for the reason the desktop
   card ladder carries one: this rule and the sheet-on rule above are the same
   weight, and naming the case that is excluded is clearer than winning a tie on
   line number. */
#ui-root.narrow.orders-on:not(.sheet-on) #orders { display: flex; }
#ui-root.narrow #log { display: none; }
/*
 * The band under the strip belongs to the hint, and the report starts below it.
 *
 * The hint is one centred line on a desktop and it is not transient: while
 * anything is selected it holds the sentence about ordering a company, for as
 * long as the selection lasts. Wrapped to a 390-pixel screen that is four lines
 * of text laid straight across the scouting report — over who is coming, how
 * many, and from which gate — which is the last readout in the game that should
 * be covered by a caption. So it is clamped to two lines and the report is
 * given the room those two lines take, whether or not one is showing: a report
 * that jumps down the screen every time a caption appears is worse than a
 * thirty-two pixel gap that does not move.
 */
#ui-root.narrow #hint { left: 6px; right: 6px; width: auto; transform: none;
  text-align: center; display: -webkit-box; -webkit-line-clamp: 2;
  -webkit-box-orient: vertical; overflow: hidden; }
#ui-root.narrow #hint:not(.show) { display: none; }
#ui-root.narrow #scouting { left: 4px; right: 4px; top: 96px; width: auto;
  max-width: none; }

/* ── The hero's five battle skills ───────────────────────────────────────────
 *
 * They are on 1–5 and they were on nothing else here: the whole hero panel was
 * hidden on this layout, so a phone player had a character who levelled, learnt
 * skills, and could never use one. (Hidden, at least, in the stylesheet — an
 * inline 'display: flex' in 'hud.ts' was beating the rule, and what was
 * actually on screen was 258 pixels of desktop panel at x = −144.)
 *
 * The row survives and the sheet above it does not. What that sheet holds — his
 * level, his bar, his kit — is the top of the character sheet the chrome bar
 * already opens, and none of it is a verb. The one exception is the seal that
 * calls him back when he is down, which exists nowhere else, so 'hero-down'
 * puts the sheet back for exactly as long as there is a body on the field.
 */
#ui-root.narrow #hero-panel { left: 4px; right: 4px; width: auto;
  bottom: calc(var(--tier) + 104px); background: none; border: none;
  box-shadow: none; }
#ui-root.narrow #hero-panel::before { display: none; }
#ui-root.narrow #hero-sheet { display: none; }
#ui-root.narrow.hero-down { --hero: 214px; }
#ui-root.narrow.hero-down #hero-sheet { display: block; max-height: 148px;
  overflow-y: auto; background: var(--panel); border: 1px solid var(--panel-edge); }
#ui-root.narrow #hero-abilities { padding: 0; gap: 6px; flex-wrap: nowrap;
  justify-content: safe center; overflow-x: auto; }

/* ── Tooltips, on a screen with no pointer ───────────────────────────────────
 *
 * Docked, not placed. A tooltip in this game runs to 330 by 180 — that is most
 * of a phone's width and a fifth of its height — and putting it at the finger
 * means putting it under the hand that asked the question, at a different
 * corner every time. So on this layout it is a band under the resource strip:
 * the same place always, capped, and scrolling when the answer is long.
 *
 * 'hud.ts''s 'positionTip' writes no inline 'left'/'top' while the narrow class
 * is on, which is the only reason these rules survive — inline beats a class,
 * and that has been the cause of three separate faults on this layout already.
 *
 * The footer is not decoration either: the box is opened by a press-and-hold,
 * which is a gesture the player has just discovered, and a panel that arrives
 * with no visible way out reads as something that has gone wrong. */
#ui-root.narrow #tip { left: 6px; right: 6px; top: 50px; max-width: none;
  max-height: 46vh; overflow-y: auto; }
#ui-root.narrow #tip.held { pointer-events: auto; }
#ui-root.narrow #tip::after { content: 'Tap anywhere to close'; display: block;
  margin-top: 8px; padding-top: 6px; border-top: 1px solid rgba(200,164,76,0.14);
  font-size: 9.5px; letter-spacing: 0.14em; text-transform: uppercase;
  color: var(--bone-deep); }

/* The strip. Ten chips, a tax dial, an alert cluster, nine chrome buttons and a
   clock do not fit in 390 pixels and never will, so the strip scrolls sideways.
   The one thing dropped is the compact speed readout, which is a number the
   two buttons either side of it already imply. The mix and the control sheet
   stay: they were dropped in the first pass on the grounds that a key list is
   meaningless without a keyboard, and that was the wrong call twice over —
   there is nowhere else on this layout to reach either of them, and the sheet
   is now where the gestures are written down. */
#ui-root.narrow #res-bar { overflow-x: auto; overflow-y: hidden; height: 44px; }
#ui-root.narrow #res-bar::-webkit-scrollbar { height: 0; }
#ui-root.narrow .res { padding: 0 9px 0 7px; gap: 4px; }
#ui-root.narrow .res .val { font-size: 13px; }
#ui-root.narrow #phase-box { padding: 0 10px; gap: 8px; }
#ui-root.narrow .ctl.named .cn { display: none; }
/*
 * A bar that scrolls has to look like one.
 *
 * Measured on a 390px phone the strip holds **1,428 pixels** of content — eight
 * chips, a tax dial, an alert cluster, nine chrome buttons and the clock — so
 * roughly a quarter of it is on screen and the rest is over the right edge with
 * nothing saying so. Reordering it was tried and is not an answer: the chips
 * alone are 460 pixels, so whatever is put first pushes something else off, and
 * putting the clock and the horn first pushed every resource in the game off.
 *
 * A fade at the right edge is the answer, and it is drawn on the strip's
 * *parent* padding rather than on the strip, so that it stays put while the
 * content slides under it.
 */
#ui-root.narrow #res-bar::after {
  content: ''; position: sticky; right: 0; flex: 0 0 18px; height: 100%;
  margin-left: -18px; pointer-events: none;
  background: linear-gradient(90deg, rgba(14,13,11,0), rgba(14,13,11,0.95));
}
/* The mix panel is placed off its button's position, and that button is on a
   bar that scrolls sideways — so on this layout it is a sheet instead, and the
   inline left/top it is given are overridden rather than fought. */
#ui-root.narrow .vol-panel { left: 8px !important; right: 8px; width: auto;
  top: auto !important; bottom: 60px; }
/* A finger is not a cursor. Everything that can be pressed gets a target it can
   actually be pressed with — 44px is the figure both platform guidelines have
   settled on and the one this layout is built to. */
#ui-root.narrow .ctl { min-width: 44px; }
#ui-root.narrow .alert { width: 34px; height: 34px; }
#ui-root.narrow .bitem { padding: 9px 8px; }
#ui-root.narrow .cat { padding: 9px 6px; }
#ui-root.narrow .seal { padding: 10px 16px; }

/* ── Company orders ──────────────────────────────────────────────────────────
 * onFormation and onStance were in the callback interface from the day
 * companies existed and nothing ever called them: the orders were on F and G
 * and nowhere else, so a shieldwall was a thing you found out about by reading
 * the title screen. */
#orders { left: 314px; right: 276px; bottom: 96px; display: none;
  align-items: center; gap: 6px; padding: 6px 10px; width: fit-content;
  max-width: 100%; margin: 0 auto; }
#ui-root.cards-on:not(.narrow) #orders { bottom: 200px; }
#orders .ow { font-size: 11px; letter-spacing: 0.1em; text-transform: uppercase;
  color: var(--gold); margin-right: 4px; }
#orders .obtn { display: inline-flex; align-items: center; gap: 7px; cursor: pointer;
  font-size: 11.5px; padding: 5px 11px; background: rgba(255,255,255,0.035);
  border: 1px solid rgba(200,164,76,0.22);
  clip-path: polygon(5px 0, 100% 0, calc(100% - 5px) 100%, 0 100%); }
#orders .obtn:hover { background: rgba(200,164,76,0.16); border-color: var(--gold); }
/* Shieldwall and Rally name one order each rather than cycling, so unlike the
   three beside them they have a state to show: lit means they are already in
   it, and pressing again is a thing you can see is unnecessary. */
#orders .obtn.on { background: rgba(200,164,76,0.22); border-color: var(--gold);
  color: var(--gold-pale); }
#orders .obtn i { font-style: normal; font-size: 8.5px; color: var(--bone-deep);
  border: 1px solid rgba(217,207,188,0.22); padding: 0 4px; }
#orders .ohint { font-size: 10.5px; color: var(--bone-deep); font-style: italic; margin-left: 6px; }

/* ── The key sheet ───────────────────────────────────────────────────────── */
.kgrid { display: grid; grid-template-columns: repeat(auto-fit, minmax(280px, 1fr)); gap: 18px; }
.kgroup { }
.kg { font-family: 'Cinzel', Georgia, serif; font-size: 11px; letter-spacing: 0.16em;
  text-transform: uppercase; color: var(--gold-pale); margin-bottom: 7px;
  border-bottom: 1px solid rgba(200,164,76,0.2); padding-bottom: 4px; }
.krow { display: flex; gap: 10px; align-items: baseline; font-size: 11.5px; padding: 3px 0;
  color: var(--bone-dim); }
.krow .kk { flex: none; min-width: 88px; color: var(--gold); font-variant-numeric: tabular-nums; }

/* ── The building panel ──────────────────────────────────────────────────────
 * Sits exactly on the build menu's ground: "what goes here" and "what is here"
 * are the same question asked of the same corner, and only one of them is ever
 * the one you want. */
#binfo { left: 0; bottom: 0; width: 306px; top: 40px; display: flex; flex-direction: column; }
#binfo .hdr { display: flex; align-items: center; justify-content: space-between; gap: 8px; }
#binfo .bi-x { cursor: pointer; color: var(--bone-deep); font-size: 13px; line-height: 1;
  padding: 0 2px; letter-spacing: 0; }
#binfo .bi-x:hover { color: var(--oxblood-light); }
#binfo .bi-body { overflow-y: auto; flex: 1; padding: 9px 11px 12px; }
#binfo .bi-flav { font-size: 11px; font-style: italic; color: var(--bone-deep); line-height: 1.45;
  margin-bottom: 9px; }
#binfo .bi-hp { height: 5px; background: rgba(0,0,0,0.6); margin-bottom: 8px; }
#binfo .bi-hp i { display: block; height: 100%; background: var(--plague); }
#binfo .bi-hp i.hurt { background: var(--oxblood-light); }
#binfo .bi-row { display: flex; justify-content: space-between; gap: 10px; font-size: 11.5px;
  padding: 3px 0; border-bottom: 1px solid rgba(255,255,255,0.04); }
#binfo .bi-row .k { color: var(--bone-deep); flex: none; }
#binfo .bi-row .v { text-align: right; font-variant-numeric: tabular-nums; }
#binfo .bi-row .v b { font-weight: 500; }
#binfo .good { color: var(--plague); }
#binfo .bad { color: var(--oxblood-light); }
#binfo .mid { color: var(--gold); }
#binfo .bi-note { font-size: 10.5px; color: var(--bone-deep); font-style: italic; line-height: 1.45;
  padding: 5px 0; }
#binfo .bi-choke { font-size: 11px; color: var(--oxblood-light); line-height: 1.45; margin: 7px 0;
  padding: 6px 8px; background: rgba(110,31,34,0.2); border-left: 2px solid var(--oxblood-light); }
#binfo .bi-why { display: flex; flex-direction: column; gap: 2px; margin: 5px 0 7px; }
#binfo .bi-why span { font-size: 10.5px; color: var(--gold-pale); background: rgba(200,164,76,0.09);
  padding: 3px 7px; }
#binfo .bi-sec { font-family: 'Cinzel', Georgia, serif; font-size: 10.5px; letter-spacing: 0.16em;
  text-transform: uppercase; color: var(--gold-deep); margin: 12px 0 5px;
  border-bottom: 1px solid rgba(200,164,76,0.16); padding-bottom: 4px; }
#binfo .bi-muster { padding: 6px 8px; margin-bottom: 3px; cursor: pointer;
  background: rgba(255,255,255,0.025); border-left: 2px solid var(--gold-deep); }
#binfo .bi-muster:hover { background: rgba(200,164,76,0.14); border-left-color: var(--gold); }
#binfo .bi-muster.off { cursor: not-allowed; opacity: 0.5; border-left-color: rgba(160,50,45,0.5); }
#binfo .bi-muster.off:hover { background: rgba(255,255,255,0.025); }
#binfo .bi-muster .mn { font-size: 12px; }
#binfo .bi-muster .mc { font-size: 10.5px; color: var(--bone-deep); font-variant-numeric: tabular-nums;
  margin-top: 2px; }
#binfo .bi-muster .mc .short { color: var(--oxblood-light); }
#binfo .bi-muster .me { font-size: 10px; color: var(--oxblood-light); margin-top: 2px; }
/* A short muster is a choice, not a refusal, so it is gold rather than oxblood:
   the button works, and this says what pressing it will actually get you. */
#binfo .bi-muster .me.short { color: var(--gold); }
#binfo .bi-acts { display: flex; gap: 6px; flex-wrap: wrap; margin-top: 14px; }
#binfo .seal.off { pointer-events: none; }

/* ── The minimap ─────────────────────────────────────────────────────────────
 * Bottom right, under the roster, which is where every game this one is trying
 * to feel like puts it. Its width is the roster's so the right-hand column
 * reads as one piece of furniture rather than two panels that happen to share
 * an edge. */
#minimap { right: 0; bottom: 0; width: 268px; }
/* See Minimap.setVisible for why this is a class and why it is important. */
#minimap.hidden { display: none !important; }
#minimap-cv { display: block; margin: 8px auto; cursor: pointer;
  border: 1px solid rgba(200,164,76,0.22); background: #14120f; }

/* ── Company roster ──────────────────────────────────────────────────────── */
/* Stops above the chart. 232 of canvas, 16 of margin, 22 of header, 1 of edge. */
#roster { right: 0; bottom: 271px; width: 268px; top: 40px; display: flex; flex-direction: column; }
#roster-list { overflow-y: auto; flex: 1; padding: 6px; }
.comp {
  padding: 6px 8px; margin-bottom: 4px; cursor: pointer;
  background: rgba(255,255,255,0.022);
  border-left: 3px solid var(--gold-deep);
  display: flex; align-items: flex-start; gap: 7px;
}
/* The head crop. flex:none because the art fills its box and a flex child
   holding an SVG will otherwise give up its width to the text beside it and
   collapse to a sliver. min-width:0 on the body for the mirror-image reason:
   a flex child's automatic minimum is its content, so a long company name
   would push the chip off the row instead of ellipsing. */
.comp .cchip { flex: none; width: 30px; height: 30px; overflow: hidden;
  border: 1px solid rgba(200,164,76,0.22); background: rgba(0,0,0,0.3); }
.comp .cchip svg { display: block; width: 100%; height: 100%; }
.comp .cbody { flex: 1; min-width: 0; }
.comp.on { background: rgba(200,164,76,0.16); border-left-color: var(--gold-pale); }
.comp .crow { display: flex; justify-content: space-between; align-items: baseline; gap: 6px; }
.comp .cname { font-size: 12px; }
.comp .crank { font-size: 9.5px; letter-spacing: 0.14em; text-transform: uppercase; color: var(--gold); }
.comp .ctype { font-size: 10.5px; color: var(--bone-deep); }
.comp .bar { height: 4px; background: rgba(0,0,0,0.55); margin-top: 4px; }
.comp .bar i { display: block; height: 100%; background: var(--gold); }
.comp .bar.hp i { background: var(--plague); }
.comp.routing { border-left-color: var(--oxblood-light); }

/* ── Unit card bar ───────────────────────────────────────────────────────── */
/*
 * The Total War card bar: one card per company, along the bottom, readable at a
 * glance without hovering. It lives between the build menu and the roster so it
 * can never sit under either, and everything else along the bottom edge is
 * lifted out of its way by the #ui-root.cards-on class rather than by inline styles —
 * one class, one layout, no per-element bookkeeping in the update loop.
 */
#cards {
  left: 314px; right: 276px; bottom: 8px;
  display: none;
  /* Safe centring, so that an army too wide for the bar overflows to the right
     where it can be scrolled to, not off the left edge where it cannot. */
  justify-content: center;
  justify-content: safe center;
  align-items: flex-end;
  gap: 4px; padding: 6px 8px;
  overflow-x: auto; overflow-y: hidden;
  /* Shrink-wrapped and centred between those two bounds rather than stretched
     across them: an army of three should not sit in the middle of a metre of
     empty panel. It grows to the bounds and then scrolls. */
  width: fit-content;
  max-width: 100%;
  margin: 0 auto;
}
#ui-root.cards-on #cards { display: flex; }
/*
 * ':not(.narrow)' on the offsets, and not on the 'display' above it.
 *
 * This ladder and the narrow one are the same weight — two ids and a class each
 * — so the tie is broken by which comes later in the file, and this block does.
 * The first version of the phone layout won that tie only because it spelled
 * its rules '#ui-root.narrow.cards-on', and the moment those were rewritten
 * against a shared rung the desktop numbers silently took the bottom of the
 * screen back: measured, the Power bar landed at 112 instead of 160 and the
 * hero's skill row was drawn straight through it.
 *
 * Naming the exclusion is better than out-weighing it. These four offsets are
 * for the layout that has columns; the phone answers the same question in its
 * own block and should not have to win an argument to do it. 'display' is left
 * alone because both layouts want exactly what it says.
 */
#ui-root.cards-on:not(.narrow) #powers { bottom: 112px; }
#ui-root.cards-on:not(.narrow) #will-bar { bottom: 190px; }
#ui-root.cards-on:not(.narrow) #log { bottom: 112px; }
#ui-root.cards-on:not(.narrow) #hero-panel { bottom: 112px; }

.ucard {
  position: relative; flex: 0 0 94px; width: 94px; padding: 3px; cursor: pointer;
  background: linear-gradient(180deg, rgba(40,36,31,0.95), rgba(16,15,13,0.95));
  border: 1px solid rgba(200,164,76,0.26);
  clip-path: polygon(6px 0, 100% 0, 100% calc(100% - 6px), calc(100% - 6px) 100%, 0 100%, 0 6px);
  transition: border-color 120ms ease, filter 120ms ease;
}
.ucard:hover { filter: brightness(1.22); border-color: var(--gold-deep); }
.ucard.on {
  border-color: var(--gold-pale);
  background: linear-gradient(180deg, rgba(84,66,30,0.95), rgba(26,22,16,0.95));
  box-shadow: 0 0 15px rgba(200,164,76,0.35);
}
.ucard.routing { border-color: var(--oxblood-light); }

/* The portrait: a hard black silhouette on the faction's colour, lit warm from
   above — the same read as the units on the field, which is the whole point.
   Its height is the only lever on how big a man is drawn: the shape is 44×40
   and the portrait is wider than it is tall, so the SVG scales to the height. */
/* The art draws its own faction niche and its own rim light, so the niche
   gradient and the drop-shadow that used to be painted under it are gone —
   doubled, they read as a halo. The two fill rules had to go with them: they
   were written for a flat silhouette and would paint the whole illustration
   solid black over a flat void. Width is explicit because the art fills its box
   rather than scaling to the height of a wider-than-tall portrait. */
.uport { position: relative; height: 52px; overflow: hidden; }
.uport .sil { display: block; width: 100%; height: 100%; }
.uport .sil svg { display: block; width: 100%; height: 100%; }

.uchev { position: absolute; top: 2px; left: 3px; display: block;
  filter: drop-shadow(0 1px 1px rgba(0,0,0,0.9)); }
.uchev path { fill: var(--gold-pale); }
.ucount { position: absolute; right: 3px; bottom: 1px; font-size: 10px;
  font-variant-numeric: tabular-nums; color: var(--bone); text-shadow: 0 1px 3px #000; }
.ucard.thin .ucount { color: var(--oxblood-light); }
.uammo { position: absolute; right: 2px; top: 3px; width: 3px; height: 26px;
  background: rgba(0,0,0,0.6); }
.uammo i { position: absolute; left: 0; right: 0; bottom: 0; background: var(--gold-pale); }
.uammo.dry i { background: var(--oxblood-light); }
.ufat { position: absolute; inset: 0; background: rgba(150,52,20,0.62); pointer-events: none; }
.ureinf {
  position: absolute; left: 2px; bottom: 1px; width: 13px; height: 13px;
  display: flex; align-items: center; justify-content: center;
  font-size: 12px; line-height: 1; color: #12100e; background: var(--gold);
  clip-path: polygon(3px 0, 100% 0, calc(100% - 3px) 100%, 0 100%);
}
.ureinf.cant { background: rgba(217,207,188,0.28); color: rgba(18,16,14,0.7); cursor: not-allowed; }

.uname { font-size: 10px; color: var(--bone-dim); padding: 2px 1px 1px;
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.ucard.on .uname { color: var(--gold-pale); }
.ubar { height: 5px; background: rgba(0,0,0,0.6); }
.ubar i { display: block; height: 100%; background: var(--plague); }
.ubar.mor { height: 3px; margin-top: 2px; }
.ubar.mor i { background: var(--gold-deep); }
.ucard.routing .ubar.mor i { background: var(--oxblood-light); }

/* ── Power bar ───────────────────────────────────────────────────────────── */
#powers { left: 50%; transform: translateX(-50%); bottom: 8px; display: flex; gap: 6px; padding: 7px; }
.power {
  position: relative; width: 58px; height: 58px; cursor: pointer;
  background: linear-gradient(180deg, rgba(36,32,28,0.95), rgba(18,16,14,0.95));
  border: 1px solid rgba(200,164,76,0.3);
  display: flex; flex-direction: column; align-items: center; justify-content: center;
  clip-path: polygon(6px 0, 100% 0, 100% calc(100% - 6px), calc(100% - 6px) 100%, 0 100%, 0 6px);
}
.power .glyph { font-size: 21px; color: var(--gold-pale); line-height: 1; }
.power .key { position: absolute; top: 2px; left: 4px; font-size: 9px; color: var(--bone-deep); }
.power .cost { position: absolute; bottom: 2px; right: 4px; font-size: 9.5px; color: var(--will); font-variant-numeric: tabular-nums; }
.power .cd {
  position: absolute; inset: 0; background: rgba(6,6,8,0.72);
  display: flex; align-items: center; justify-content: center;
  font-size: 15px; font-variant-numeric: tabular-nums; color: var(--bone-dim);
}
.power.armed { border-color: var(--gold-pale); box-shadow: 0 0 18px rgba(200,164,76,0.4); }
.power.cant { filter: grayscale(0.7) brightness(0.6); }

/* Clear of the Power bar, which is 74px tall and was sitting on top of it: the
   Will bar was drawn behind the panel and only its two ends were ever visible,
   so the one readout that tells you whether you can cast anything could not be
   read, let alone hovered. */
#will-bar { position: absolute; left: 50%; transform: translateX(-50%); bottom: 86px; width: 320px; height: 7px;
  background: rgba(0,0,0,0.65); border: 1px solid rgba(127,168,212,0.3);
  /* Hoverable: it is a bare bar with no number on it, so the number has to be
     one pointer-move away. #ui-root is pointer-events: none by default. */
  pointer-events: auto; cursor: help;
  clip-path: polygon(5px 0, 100% 0, calc(100% - 5px) 100%, 0 100%); }
#will-bar i { display: block; height: 100%; background: linear-gradient(90deg, #3f6f9e, var(--will)); }
#will-label { position: absolute; left: 50%; transform: translateX(-50%); bottom: 97px;
  font-size: 9.5px; letter-spacing: 0.2em; text-transform: uppercase;
  color: var(--bone-dim); white-space: nowrap; pointer-events: none;
  font-variant-numeric: tabular-nums; text-shadow: 0 1px 2px rgba(0,0,0,0.8); }
#ui-root.cards-on:not(.narrow) #will-label { bottom: 201px; }

/* ── The hero ────────────────────────────────────────────────────────────── */
#hero-panel { right: 276px; bottom: 8px; width: 258px; display: flex; flex-direction: column; }
#hero-panel .hdr { display: flex; align-items: center; justify-content: space-between; gap: 8px; }
#hero-panel .hdr .pts {
  font-family: 'Inter', sans-serif; font-size: 10px; letter-spacing: 0; text-transform: none;
  color: #12100e; background: var(--gold); padding: 1px 6px; font-weight: 600;
  clip-path: polygon(4px 0, 100% 0, calc(100% - 4px) 100%, 0 100%);
}
#hero-panel .hbody { padding: 7px 9px 9px; }
#hero-panel .hline { display: flex; justify-content: space-between; align-items: baseline; font-size: 11.5px; }
#hero-panel .hlvl { font-size: 10px; letter-spacing: 0.14em; text-transform: uppercase; color: var(--gold); }
#hero-panel .hbar { height: 5px; background: rgba(0,0,0,0.6); margin-top: 5px; }
#hero-panel .hbar i { display: block; height: 100%; background: var(--gold); }
#hero-panel .hbar.hp i { background: var(--plague); }
#hero-panel .hbar.xp i { background: linear-gradient(90deg, #6b5320, var(--gold-pale)); }
#hero-panel .hpassive { font-size: 10.5px; color: var(--bone-deep); line-height: 1.4; margin: 7px 0 8px; }
#hero-panel .hdown { font-size: 11px; color: var(--oxblood-light); margin: 6px 0; line-height: 1.4; }
#hero-panel .seal { width: 100%; }

#hero-abilities { display: flex; gap: 6px; padding: 0 9px 9px; flex-wrap: wrap; }
#hero-abilities:empty { display: none; }
.power.hero { width: 46px; height: 46px; border-color: rgba(200,164,76,0.5); }
.power.hero .glyph { font-size: 17px; color: var(--gold); }

/* A locked skill is drawn, not hidden: the bar is also the levelling plan. */
.power.hero .lock { position: absolute; inset: 0; display: none;
  align-items: center; justify-content: center; font-size: 11px; font-weight: 500;
  letter-spacing: 0.04em; color: var(--bone-dim); background: rgba(10,9,8,0.72); }
.power.hero.locked { border-color: rgba(217,207,188,0.14); cursor: help; }
.power.hero.locked .glyph { opacity: 0.22; }
/* Level reached, point unspent — worth noticing, so it pulses gold. */
.power.hero.ready-to-learn { border-color: rgba(200,164,76,0.85); }
.power.hero.ready-to-learn .lock { color: var(--gold); background: rgba(10,9,8,0.5);
  animation: hero-ready 1.9s ease-in-out infinite; }
@keyframes hero-ready { 0%,100% { opacity: 0.55; } 50% { opacity: 1; } }

/* The tree, laid out on the row/col the data already carries. */
.hsheet-top { display: flex; gap: 22px; font-size: 12px; margin-bottom: 12px; color: var(--bone-dim); }
.hsheet-top b { color: var(--bone); font-variant-numeric: tabular-nums; }
#modal .hpassive {
  font-size: 11.5px; color: var(--bone-dim); line-height: 1.5; margin-bottom: 16px;
  padding: 8px 10px; background: rgba(0,0,0,0.3); border-left: 2px solid var(--gold-deep);
}
.htree { display: grid; gap: 10px; }
.hskill {
  padding: 10px 11px; cursor: default; background: rgba(255,255,255,0.025);
  border: 1px solid rgba(200,164,76,0.16);
  clip-path: polygon(8px 0, 100% 0, 100% calc(100% - 8px), calc(100% - 8px) 100%, 0 100%, 0 8px);
}
.hskill .sn { font-family: 'Cinzel', Georgia, serif; font-size: 12px; letter-spacing: 0.08em; color: var(--gold); }
.hskill .sr { font-size: 11px; line-height: 1.45; margin-top: 4px; }
.hskill .sm { font-size: 10px; letter-spacing: 0.14em; text-transform: uppercase; color: var(--bone-deep); margin-top: 6px; }
.hskill .sf { font-size: 10.5px; font-style: italic; color: var(--bone-deep); margin-top: 6px; line-height: 1.4; }
.hskill.open { cursor: pointer; border-color: var(--gold); background: rgba(200,164,76,0.1); }
.hskill.open:hover { background: rgba(200,164,76,0.2); }
.hskill.open .sm { color: var(--gold-pale); }
.hskill.owned { border-color: rgba(125,148,54,0.5); background: rgba(125,148,54,0.1); }
.hskill.owned .sm { color: var(--plague); }
.hskill.shut { opacity: 0.45; }

/* ── Combat log ──────────────────────────────────────────────────────────── */
#log { left: 314px; bottom: 8px; width: 330px; max-height: 168px; overflow: hidden;
  display: flex; flex-direction: column-reverse; pointer-events: none; padding: 4px 8px; }
#log div { font-size: 11.5px; line-height: 1.5; color: var(--bone-dim); text-shadow: 0 1px 3px #000; }
#log div.good { color: var(--plague); }
#log div.bad { color: var(--oxblood-light); }
#log div.dire { color: #e0553f; font-weight: 600; }
#log div.lore { color: var(--gold-pale); font-style: italic; }

/* ── Tooltip ─────────────────────────────────────────────────────────────── */
#tip {
  position: absolute; max-width: 330px; padding: 9px 11px; pointer-events: none;
  background: rgba(12,11,10,0.97); border: 1px solid var(--panel-edge);
  box-shadow: 0 10px 30px rgba(0,0,0,0.7); z-index: 60; display: none;
}
#tip .tname { font-family: 'Cinzel', Georgia, serif; font-size: 12px; letter-spacing: 0.12em;
  text-transform: uppercase; color: var(--gold); margin-bottom: 4px; }
#tip .trule { font-size: 11.5px; line-height: 1.5; color: var(--bone); }
#tip .tstat { font-size: 11px; color: var(--bone-dim); font-variant-numeric: tabular-nums; margin-top: 5px; line-height: 1.6; }
#tip .tstat b { color: var(--bone); font-weight: 500; }
/* A price the treasury cannot meet, quoted inside a tooltip. */
#tip .short { color: var(--oxblood-light); }
#tip .tflav { font-size: 11px; font-style: italic; color: var(--bone-deep); margin-top: 6px;
  padding-top: 6px; border-top: 1px solid rgba(200,164,76,0.14); line-height: 1.45; }

/* ── Scouting panel ──────────────────────────────────────────────────────── */
#scouting { left: 314px; top: 48px; width: 344px; }
#scouting .body { padding: 9px 11px; max-height: calc(100vh - 132px); overflow-y: auto; }
.scout-row { display: flex; justify-content: space-between; gap: 8px; font-size: 11.5px; padding: 2.5px 0; }
.scout-row .k { color: var(--bone-deep); }
.scout-row .v { text-align: right; }
/*
 * The muster list on the deployment screen. A row is a button, so it has to
 * look like one: it lights on hover and it goes plainly dead when the purse
 * cannot cover it. The dead state is pointer-events:none as well as dim,
 * because onScoutingClick refuses .off anyway, and a cursor that still
 * changes over a row that does nothing is a small lie.
 */
.scout-row.lo { cursor: pointer; padding: 3px 6px; margin: 0 -6px; border-radius: 2px; }
.scout-row.lo:hover { background: rgba(255,255,255,0.05); }
.scout-row.lo:hover .k { color: var(--bone); }
.scout-row.lo.off { opacity: 0.34; pointer-events: none; }
.scout-comp { font-size: 11.5px; padding: 3px 0; border-top: 1px solid rgba(255,255,255,0.05); }
.scout-comp .n { color: var(--bone); }
.scout-comp .note { color: var(--gold-deep); font-size: 10.5px; font-style: italic; }
.scout-mod { font-size: 11px; color: var(--ember); padding: 3px 0; }
.scout-champ { margin-top: 8px; padding: 7px; background: rgba(110,31,34,0.22); border: 1px solid rgba(160,50,45,0.4); }
.scout-champ .cn { font-family: 'Cinzel', Georgia, serif; font-size: 12px; color: #e8a99a; letter-spacing: 0.08em; }
.scout-champ .ct { font-size: 10px; letter-spacing: 0.24em; color: #d1352c; text-transform: uppercase; margin: 2px 0 4px; }
.scout-champ .cb { font-size: 11px; color: var(--bone-dim); line-height: 1.45; }

/* ── Boss name card ──────────────────────────────────────────────────────── */
#boss-card {
  position: absolute; left: 50%; top: 26%; transform: translateX(-50%);
  text-align: center; pointer-events: none; z-index: 40;
  opacity: 0; transition: opacity 400ms ease;
}
#boss-card.show { opacity: 1; }
#boss-card .bn { font-family: 'Cinzel', Georgia, serif; font-size: 34px; letter-spacing: 0.1em;
  color: #f0d8a8; text-shadow: 0 0 40px rgba(0,0,0,0.95), 0 3px 10px #000; }
#boss-card .bt { font-family: 'Cinzel', Georgia, serif; font-size: 14px; letter-spacing: 0.5em;
  color: #d1352c; margin-top: 8px; text-shadow: 0 2px 10px #000; }
#vignette {
  position: absolute; inset: 0; pointer-events: none; z-index: 35; opacity: 0;
  transition: opacity 600ms ease;
  background: radial-gradient(ellipse at center, rgba(0,0,0,0) 42%, rgba(60,10,8,0.55) 100%);
}
#vignette.show { opacity: 1; }

/* ── Floating world text ─────────────────────────────────────────────────── */
#world-text { position: absolute; inset: 0; pointer-events: none; overflow: hidden; z-index: 20; }
#world-text span {
  position: absolute; font-variant-numeric: tabular-nums; font-weight: 600;
  text-shadow: 0 2px 4px rgba(0,0,0,0.95); white-space: nowrap;
  transform: translate(-50%, -50%);
}

/* ── Modal (relics, spoils, menus) ───────────────────────────────────────── */
#modal {
  position: absolute; inset: 0; z-index: 70; display: none;
  align-items: center; justify-content: center;
  background: rgba(6,6,7,0.82); pointer-events: auto;
}
#modal.show { display: flex; }
#modal .sheet { width: min(920px, 92vw); max-height: 86vh; overflow-y: auto;
  background: var(--panel); border: 1px solid var(--panel-edge); padding: 22px 26px;
  box-shadow: 0 30px 90px rgba(0,0,0,0.8); }
#modal h1 { font-size: 20px; letter-spacing: 0.26em; margin-bottom: 4px; }
/* The pause menu's three doors. Styled here rather than borrowing the title
   screen's seal class, which lives in the menu's own stylesheet and is not
   loaded on this side — a button that inherits nothing renders as bare text. */
#modal .pausebtns { display: flex; gap: 12px; flex-wrap: wrap; margin-top: 22px; }
#modal .pausebtns .seal { cursor: pointer; padding: 11px 22px; font-size: 12px;
  letter-spacing: 0.2em; text-transform: uppercase; color: var(--bone);
  background: rgba(110,31,34,0.5); border: 1px solid rgba(200,164,76,0.4);
  clip-path: polygon(8px 0, 100% 0, calc(100% - 8px) 100%, 0 100%); }
#modal .pausebtns .seal:hover { background: rgba(110,31,34,0.75); color: var(--gold-pale); }
#modal .pausebtns .seal.ghost { background: rgba(255,255,255,0.04);
  border-color: rgba(200,164,76,0.22); color: var(--bone-dim); }
#modal .pausebtns .seal.ghost:hover { background: rgba(200,164,76,0.12); color: var(--bone); }
#modal .sub { font-size: 12px; color: var(--bone-deep); font-style: italic; margin-bottom: 18px; }
.cards { display: grid; grid-template-columns: repeat(auto-fit, minmax(220px, 1fr)); gap: 12px; }
.card {
  padding: 14px; cursor: pointer; background: rgba(255,255,255,0.03);
  border: 1px solid rgba(200,164,76,0.2);
  clip-path: polygon(10px 0, 100% 0, 100% calc(100% - 10px), calc(100% - 10px) 100%, 0 100%, 0 10px);
  transition: background 140ms ease, border-color 140ms ease;
}
.card:hover { background: rgba(200,164,76,0.13); border-color: var(--gold); }
.card .cn { font-family: 'Cinzel', Georgia, serif; font-size: 13px; color: var(--gold); letter-spacing: 0.1em; margin-bottom: 6px; }
.card .cr { font-size: 11.5px; line-height: 1.5; }
.card .cf { font-size: 11px; font-style: italic; color: var(--bone-deep); margin-top: 8px; line-height: 1.45; }
.card .crar { font-size: 9.5px; letter-spacing: 0.2em; text-transform: uppercase; color: var(--bone-deep); margin-top: 8px; }

/* Chronicle: the flavour paragraph sits in the sheet, not in a card. */
#modal .sheet > .cf { font-size: 12.5px; font-style: italic; color: var(--bone-dim); line-height: 1.6; max-width: 72ch; }
/* A choice the treasury cannot cover. Shown, priced, and not clickable. */
.card.locked { cursor: not-allowed; opacity: 0.42; border-color: rgba(160,50,45,0.35); }
.card.locked:hover { background: rgba(255,255,255,0.03); border-color: rgba(160,50,45,0.35); }
.card.locked .crar { color: var(--ember); }

/* ── Debug ───────────────────────────────────────────────────────────────── */
#fps { position: absolute; top: 44px; right: 274px; font-size: 11px; font-variant-numeric: tabular-nums;
  color: var(--bone-deep); text-align: right; background: rgba(10,9,8,0.7); padding: 5px 8px;
  line-height: 1.6; pointer-events: none; display: none; white-space: pre; }
#fps.show { display: block; }
#console { position: absolute; left: 0; right: 0; top: 0; height: 190px; background: rgba(6,8,6,0.95);
  border-bottom: 1px solid rgba(125,148,54,0.4); display: none; flex-direction: column; z-index: 80; pointer-events: auto; }
#console.show { display: flex; }
#console-out { flex: 1; overflow-y: auto; padding: 8px 10px; font-family: ui-monospace, Consolas, monospace;
  font-size: 11.5px; color: #9fb46a; line-height: 1.5; white-space: pre-wrap; }
#console-in { border: none; border-top: 1px solid rgba(125,148,54,0.3); background: rgba(0,0,0,0.5);
  color: #cfe08a; font-family: ui-monospace, Consolas, monospace; font-size: 12.5px; padding: 8px 10px; outline: none; }

/* ── Hint strip ──────────────────────────────────────────────────────────── */
#hint { position: absolute; left: 50%; transform: translateX(-50%); top: 46px;
  font-size: 11.5px; letter-spacing: 0.06em; color: var(--gold-pale);
  background: rgba(10,9,8,0.85); padding: 5px 14px; border: 1px solid rgba(200,164,76,0.25);
  clip-path: polygon(8px 0, 100% 0, calc(100% - 8px) 100%, 0 100%); display: none; }
#hint.show { display: block; }

/* ── Kit: the hero's slots and the cart, on the character sheet ──────────── */
#hero-sheet .hkit { font-size: 10.5px; letter-spacing: 0.05em; color: var(--bone-deep);
  margin: 4px 0 2px; }
.ksec { font-family: 'Cinzel', Georgia, serif; font-size: 13px; letter-spacing: 0.12em;
  text-transform: uppercase; color: var(--gold-pale); margin: 18px 0 8px;
  border-bottom: 1px solid rgba(200,164,76,0.2); padding-bottom: 5px; }
.ksec .dim { font-family: 'Inter', system-ui, sans-serif; font-size: 11px;
  letter-spacing: 0.04em; text-transform: none; color: var(--bone-deep); }
.kitslots { display: grid; grid-template-columns: repeat(auto-fit, minmax(230px, 1fr)); gap: 8px; }
.kitcart { display: grid; grid-template-columns: repeat(auto-fit, minmax(230px, 1fr)); gap: 8px; }
.kitcart.empty { display: block; font-size: 11.5px; padding: 8px 2px; }
.kitslot, .kititem { background: rgba(10,9,8,0.5); border: 1px solid rgba(200,164,76,0.18);
  padding: 8px 10px; clip-path: polygon(7px 0, 100% 0, 100% calc(100% - 7px), calc(100% - 7px) 100%, 0 100%, 0 7px); }
.kitslot.empty { border-style: dashed; border-color: rgba(200,164,76,0.10); }
.kitslot .ks { font-family: 'Cinzel', Georgia, serif; font-size: 10px; letter-spacing: 0.16em;
  text-transform: uppercase; color: var(--gold-deep); margin-bottom: 3px; }
.kitslot .kn, .kititem .kn { font-size: 13px; color: var(--bone); }
.kitslot .kn.dim { font-size: 11.5px; color: var(--bone-deep); font-style: italic; }
.klv { font-size: 10.5px; letter-spacing: 0.06em; color: var(--gold-deep); }
.kfx { font-size: 11px; color: var(--gold-pale); margin-top: 4px; line-height: 1.5; }
.kf { font-size: 10.5px; color: var(--bone-deep); font-style: italic; margin-top: 3px; line-height: 1.45; }
.kbtn { display: flex; flex-wrap: wrap; gap: 5px; margin-top: 7px; }
.seal.tiny { font-size: 10px; padding: 3px 8px; letter-spacing: 0.08em; }
.seal.tiny.disabled { opacity: 0.4; cursor: default; }

/* ── The scrollbars ──────────────────────────────────────────────────────────
 *
 * Nearly every panel in this game is taller than the box it is given — the
 * build list is 2,555px in 736, the Ledger's Powers tab 3,310px in 677 — so the
 * scrollbar is not an edge case here, it is the *only* thing that distinguishes
 * "the list ends here" from "the list continues and you may go there". At
 * 8px of 25%-alpha gold on a near-black track it was neither, and a sheet that
 * cuts a card in half at the fold with no rail beside it reads as clipped.
 *
 * So: wide enough to be a target, and a thumb with enough contrast to be seen
 * against the panel rather than lost in it. The track keeps a hairline edge so
 * the *channel* is visible even when the thumb is nearly the full height — that
 * is what says "this is a scrolling box" when there is only a little to scroll.
 *
 * ── The trap, if you are judging this from a screenshot ──────────────────────
 *
 * **Headless Chromium draws no scrollbar at all.** It uses overlay scrollbars,
 * which take zero layout width and ignore these rules entirely; offsetWidth -
 * clientWidth is 0 in the harness and 8 in a real browser, and no
 * --disable-features=OverlayScrollbar flag changes it. Every picture in
 * artifacts/shots/ is therefore missing every scrollbar in the game, and a
 * panel that looks clipped in one is very often a panel that scrolls perfectly
 * well for the player.
 *
 * HEARTH_HEADED=1 npm run shots shoots with a real browser and real
 * scrollbars. Judge "is this clipped?" from that, never from the default run.
 */
::-webkit-scrollbar { width: 10px; height: 8px; }
::-webkit-scrollbar-track { background: rgba(0,0,0,0.34); border-left: 1px solid rgba(200,164,76,0.12); }
::-webkit-scrollbar-thumb { background: rgba(200,164,76,0.42); border: 1px solid rgba(200,164,76,0.16); }
::-webkit-scrollbar-thumb:hover { background: rgba(200,164,76,0.62); }
::-webkit-scrollbar-corner { background: rgba(0,0,0,0.34); }

/* ── "There is more below this" ──────────────────────────────────────────────
 *
 * The scrollbar above is the right answer for a player and no answer at all for
 * a screenshot, because the harness cannot draw one. This is the same statement
 * made in ordinary paint, so it survives being photographed: a soft shadow at
 * whichever edge has content beyond it.
 *
 * No JavaScript and no scroll listener — which matters, because everything in
 * this file is redrawn on a loop and a class toggled from a scroll handler is
 * one more thing to get wrong.
 *
 * The glow is gold rather than the black this idiom usually uses. A drop shadow
 * says "there is more" by darkening the edge, which needs a light page to read
 * against; every panel here is already near-black, and the first attempt was
 * invisible in the screenshot it was written for. Warm-on-dark is the contrast
 * this palette actually has, and gold is already the game's word for an edge.
 *
 * It is done with background-attachment:
 * the two *cover* layers are attached local, so they travel with the content
 * and sit over the shadow when the box is at that end; the two *shadow* layers
 * are attached scroll, so they stay pinned to the box. Scroll away from an edge
 * and its cover slides off, uncovering the shadow. Scroll back and it hides it
 * again. The effect is therefore exactly true by construction: a shadow appears
 * at an edge if and only if there is content past it.
 *
 * The covers must be the colour the box is painted, which is why this is scoped
 * to the boxes whose backdrop is known rather than applied to everything that
 * scrolls. The world map's columns are painted over a radial gradient and are
 * deliberately not in this list; they would need a cover that changes down the
 * panel, and a band of the wrong grey is worse than no shadow.
 */
#build-list, #roster-list, #binfo .bi-body, #modal .sheet {
  background-image:
    linear-gradient(to bottom, rgb(18,16,14) 64%, rgba(18,16,14,0)),
    linear-gradient(to top, rgb(18,16,14) 64%, rgba(18,16,14,0)),
    linear-gradient(to bottom, rgba(200,164,76,0.26), rgba(200,164,76,0)),
    linear-gradient(to top, rgba(200,164,76,0.26), rgba(200,164,76,0));
  background-position: 0 0, 0 100%, 0 0, 0 100%;
  background-size: 100% 26px, 100% 26px, 100% 16px, 100% 16px;
  background-repeat: no-repeat;
  background-attachment: local, local, scroll, scroll;
}

/* ── buildui: the Age axis in the build list and the drill list ─────────────
 *
 * Both lists are now grouped under an Age heading, and the heading carries the
 * gate: a row under NOT YET does not need to repeat the refusal on itself. Four
 * tones, because there are exactly four things an Age can be relative to where
 * the player is standing — behind them, under them, next, or far off — and the
 * eye should be able to find "next" without reading a word of it.
 *
 * The headings are sticky. In a 306px column at the fifth Age a build list runs
 * to five groups and the scroll loses the reader otherwise: the answer to
 * "which Age is this row" must never be above the fold. */
.bage, #binfo .bi-age {
  display: flex; align-items: baseline; gap: 6px;
  position: sticky; top: 0; z-index: 2;
  margin: 9px 0 4px; padding: 4px 8px 4px 6px;
  background: rgb(18,16,14);
  border-bottom: 1px solid rgba(200,164,76,0.16);
}
.bage:first-child, #binfo .bi-age:first-child { margin-top: 2px; }
.bage .ba-n, #binfo .bi-age .ba-n {
  font-family: 'Cinzel', Georgia, serif; font-size: 9.5px; letter-spacing: 0.16em;
  text-transform: uppercase; color: var(--gold-deep);
}
.bage .ba-a, #binfo .bi-age .ba-a {
  font-size: 11.5px; color: var(--bone); letter-spacing: 0.04em;
}
.bage .ba-s, #binfo .bi-age .ba-s {
  margin-left: auto; font-size: 9.5px; letter-spacing: 0.1em;
  text-transform: uppercase; color: var(--bone-deep);
}

/* Behind you: the heading is a divider, not an announcement. */
.bage.past, #binfo .bi-age.past { border-bottom-color: rgba(200,164,76,0.08); }
.bage.past .ba-n, #binfo .bi-age.past .ba-n { color: rgba(200,164,76,0.4); }
.bage.past .ba-a, #binfo .bi-age.past .ba-a { color: var(--bone-deep); }

/* Where you are: the one heading in the column that is lit. */
.bage.now, #binfo .bi-age.now {
  background: linear-gradient(90deg, rgba(200,164,76,0.13), rgb(18,16,14) 70%);
  border-bottom-color: rgba(200,164,76,0.4);
}
.bage.now .ba-n, #binfo .bi-age.now .ba-n { color: var(--gold); }
.bage.now .ba-a, #binfo .bi-age.now .ba-a { color: var(--gold-pale); }
.bage.now .ba-s, #binfo .bi-age.now .ba-s { color: var(--gold-deep); }

/* The Age above: shut, and saying so in the one colour this UI uses for shut. */
.bage.next, #binfo .bi-age.next { border-bottom-color: rgba(160,50,45,0.45); }
.bage.next .ba-n, #binfo .bi-age.next .ba-n { color: rgba(200,164,76,0.5); }
.bage.next .ba-s, #binfo .bi-age.next .ba-s { color: var(--oxblood-light); }
.bage.far, #binfo .bi-age.far { border-bottom-color: rgba(255,255,255,0.06); }
.bage.far .ba-n, #binfo .bi-age.far .ba-n { color: rgba(200,164,76,0.32); }
.bage.far .ba-a, #binfo .bi-age.far .ba-a { color: var(--bone-deep); }
.bage.far .ba-s, #binfo .bi-age.far .ba-s { color: rgba(255,255,255,0.28); }

/* A locked entry. Dimmer than unaffordable, because unaffordable is a thing
 * that will pass on its own and this is not — but not so dim that the cost
 * line stops being readable, since the red half of that cost line is the whole
 * lesson: those are the goods you have never held. */
.bitem.locked, #binfo .bi-muster.locked {
  opacity: 0.55; cursor: default; border-left-color: rgba(160,50,45,0.35);
}
.bitem.locked:hover { background: rgba(255,255,255,0.022); border-left-color: rgba(160,50,45,0.35); }
.bitem.locked .bname { color: var(--bone-deep); }
.bitem .block {
  font-size: 10px; letter-spacing: 0.02em; color: var(--oxblood-light); margin-top: 3px;
}

/* The fold. A row, not a button: it is the end of the list, and it should read
 * as the place the list stops rather than as another thing to build. */
.bmore, #binfo .bi-more {
  margin: 8px 0 4px; padding: 7px 8px; cursor: pointer;
  font-size: 10.5px; letter-spacing: 0.06em; color: var(--bone-deep);
  text-align: center; background: rgba(255,255,255,0.02);
  border: 1px dashed rgba(200,164,76,0.18);
}
.bmore:hover, #binfo .bi-more:hover { color: var(--gold-pale); border-color: rgba(200,164,76,0.4); }

/* The mix panel — three sliders under the ♪, remembered between sessions. */
.vol-panel {
  position: absolute; z-index: 60; width: 190px; padding: 10px 12px;
  background: rgba(16,14,12,0.96); border: 1px solid var(--gold-deep);
  clip-path: polygon(8px 0, 100% 0, 100% calc(100% - 8px), calc(100% - 8px) 100%, 0 100%, 0 8px);
}
.vol-row { display: flex; align-items: center; gap: 8px; margin: 5px 0; }
.vol-row span { flex: 0 0 58px; font-size: 10px; letter-spacing: 0.1em;
  text-transform: uppercase; color: var(--bone-dim); }
.vol-row input[type='range'] { flex: 1; accent-color: var(--gold); height: 14px; }
.vol-row input[type='checkbox'] { accent-color: var(--gold); }
.vol-cap { margin-top: 9px; padding-top: 7px; border-top: 1px solid rgba(200,164,76,0.18);
  font-size: 9.5px; letter-spacing: 0.2em; text-transform: uppercase; color: var(--gold-deep); }
`;

export function injectStyle(): void {
  const el = document.createElement('style');
  el.id = 'hearth-style';
  el.textContent = UI_CSS;
  document.head.appendChild(el);
}
